<script lang="ts" setup>
import FinishDialog from "@/views/trips/components/dialogs/FinishDialog.vue";
import CheckInDialog from "@/views/trips/components/dialogs/CheckInDialog.vue";
import DeliveryInfoDialog from "@/views/trips/view/dialogs/DeliveryInfoDialog.vue";
import PickupInfoDialog from "@/views/trips/view/dialogs/PickupInfoDialog.vue";
import GoodToGoDialog from "@/views/trips/components/dialogs/GoodToGoDialog.vue";
import { computed, ref, watch } from "vue";
import { OrderStopType } from "@/data/order";
import { RouteStopStatus } from "@/data/trip";
import type { TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import { API } from "@/api/api";
import type { RouteStopDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { useSnackbarStore } from "@/store/snackbar.store";

const api = new API();
const { showSnackbarError } = useSnackbarStore();

const showActionDialog = defineModel<boolean>("showActionDialog", { default: false });
const showInfoDialog = defineModel<boolean>("showInfoDialog", { default: false });
defineModel<boolean>("loading", { default: false });

const props = defineProps<{
  tripId: number;
  tripMonitorRoute: TripMonitorRoute;
}>();

const emits = defineEmits<{
  (e: "update:showActionDialog", value: boolean): void;
  (e: "update:showInfoDialog", value: boolean): void;
  (e: "update:loading", value: boolean): void;
}>();

const route = ref<TripRouteDto | null>(null);
const stop = ref<RouteStopDto | null>(null);

const isPickup = computed(() => {
  return props.tripMonitorRoute.nextRouteStop?.type === OrderStopType.pickup;
});

const status = computed(() => {
  return props.tripMonitorRoute.nextRouteStop?.status;
});

const isReadyForCheckIn = computed(() => {
  return status.value === RouteStopStatus.inRoute;
});

const isReadyForLoading = computed(() => {
  return status.value === RouteStopStatus.waitingForLoading && isPickup.value;
});

const isReadyForLoadingVerification = computed(() => {
  return status.value === RouteStopStatus.waitingForVerification && isPickup.value;
});

const isReadyForUnloading = computed(() => {
  return status.value === RouteStopStatus.waitingForLoading && !isPickup.value;
});

const isReadyForUnloadingVerification = computed(() => {
  return status.value === RouteStopStatus.waitingForVerification && !isPickup.value;
});

const isReadyForGoodToGo = computed(() => {
  return status.value === RouteStopStatus.waitingForGoodToGo;
});

async function loadRouteInfo() {
  if (!props.tripMonitorRoute.id || !props.tripId) {
    return;
  }

  try {
    const response = await api.trips.getRouteByTripId(props.tripId, props.tripMonitorRoute.id);

    if (response.data) {
      route.value = response.data;
      stop.value =
        route.value.routeStops.find((routeStop) => routeStop.id === props.tripMonitorRoute.nextRouteStop?.id) || null;
    }
  } catch (error) {
    showSnackbarError("Could not get route by trip id");
  }
}

watch(
  [showActionDialog, showInfoDialog],
  async (value) => {
    const hasActive = value.filter((item) => item).length > 0;

    if (hasActive) {
      emits("update:loading", true);
      await loadRouteInfo();
      emits("update:loading", false);
    } else {
      route.value = null;
      stop.value = null;
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div v-if="route && stop" class="action-dialogs">
    <template v-if="showActionDialog">
      <check-in-dialog
        v-if="isReadyForCheckIn"
        v-model="showActionDialog"
        :stop="stop"
        :trip-id="tripId"
        :truck="tripMonitorRoute.truck"
        class="mt-2"
      />

      <pickup-info-dialog
        v-if="isReadyForLoading || isReadyForLoadingVerification"
        v-model="showActionDialog"
        :preload="isReadyForLoadingVerification"
        :route="route"
        :stop="stop"
        :trip-id="tripId"
        :truck="tripMonitorRoute.truck"
        title="Verify information"
      />

      <delivery-info-dialog
        v-if="isReadyForUnloading || isReadyForUnloadingVerification"
        v-model="showActionDialog"
        :preload="isReadyForUnloadingVerification"
        :route="route"
        :stop="stop"
        :trip-id="tripId"
        title="Verify information"
      ></delivery-info-dialog>
    </template>

    <template v-if="isReadyForGoodToGo">
      <template v-if="showActionDialog">
        <good-to-go-dialog
          v-if="!tripMonitorRoute.nextRouteStop?.isLast"
          v-model="showActionDialog"
          :stop="stop"
          :trip-id="tripId"
          :truck="tripMonitorRoute.truck"
        />

        <finish-dialog
          v-else
          v-model="showActionDialog"
          :stop="stop"
          :trip-id="tripId"
          :truck-id="tripMonitorRoute.truck.id"
        />
      </template>

      <template v-if="showInfoDialog">
        <pickup-info-dialog
          v-if="isPickup"
          v-model="showInfoDialog"
          :route="route"
          :stop="stop"
          :trip-id="tripId"
          preload
          readonly
          title="Pick up information"
        ></pickup-info-dialog>

        <delivery-info-dialog
          v-else
          v-model="showInfoDialog"
          :route="route"
          :stop="stop"
          :trip-id="tripId"
          preload
          readonly
          title="Delivery information"
        ></delivery-info-dialog>
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped></style>
